import { useState } from 'react'
import styled from 'styled-components'

import { customEvent } from '@fe-toolkit/eventing'
import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  Link,
  OptionRadio,
  RadioGroup,
  theme,
  Typography,
  UnorderedList,
} from '@fe-toolkit/web-components/ssr'

import content from '@/content/content'
import { buildSimOnlyRedirectPageURL } from '@/utils/buildSimOnlyRedirectPageURL'
import EsimCompatibilityModal from '@/components/molecules/EsimCompatibilityModal'
import WhatsAnEsimModal from '@/components/molecules/WhatsAnEsimModal'
import CanIkeepMyNumberModal from '@/components/molecules/CanIkeepMyNumberModal'
import LinkListCard from '@/components/molecules/LinkListCard'
import { SimType } from '@/types'
import { mfeRedirect } from '@/utils/mfeRedirect'
import { ROUTE } from '@/constants'
import { snowplowEvents } from '@/utils/snowplow'

const SIM_ORDER_REFERRER_QUERY_PARAM_KEY = 'referrer'
const SIM_ORDER_REFERRER_QUERY_PARAM_VALUE = 'simorder'

/**
 * Logged-in members go straight to the SimOnlyRedirectPage. The auth page sends
 * any logged-in visitor to its stay in touch step, which would ask them for
 * their marketing preferences again on every SIM order.
 * @param sku
 * @param selectedSimType
 * @param isLoggedIn
 * @returns URL path and query params for the auth page, with SimOnlyRedirectPage as redirect,
 * or the SimOnlyRedirectPage itself for logged-in members
 * @example
 * buildNextPageUrl('BD075', 'eSIM', false)
 * returns '/auth/register?referrer=simorder&redirect=%2Fsim-only%2Fredirect%2FBD075%3FselectedSimType%3DeSIM'
 * @example
 * buildNextPageUrl('BD075', 'eSIM', true)
 * returns '/sim-only/redirect/BD075?selectedSimType=eSIM'
 */
export function buildNextPageUrl(
  sku: string,
  selectedSimType: SimType,
  isLoggedIn: boolean,
): string {
  const redirectUrl = buildSimOnlyRedirectPageURL(sku, selectedSimType)

  if (isLoggedIn) return redirectUrl

  const queryParams = new URLSearchParams()
  queryParams.set(
    SIM_ORDER_REFERRER_QUERY_PARAM_KEY,
    SIM_ORDER_REFERRER_QUERY_PARAM_VALUE,
  )
  queryParams.set('redirect', redirectUrl)

  return `${ROUTE.CREATE_ACCOUNT_ENDPOINT}?${queryParams.toString()}`
}

export type ChooseSimProps = {
  sku: string
  enableSnowplow?: boolean
  isLoggedIn?: boolean
}

const StyledTypography = styled(Typography)`
  text-wrap: pretty;
`

const ChooseSim = ({
  sku,
  enableSnowplow,
  isLoggedIn = false,
}: ChooseSimProps) => {
  const [chosenSim, setChosenSim] = useState<SimType>('eSIM')
  const [showEsimCompatibleModal, setShowEsimCompatibleModal] = useState(false)
  const [showWhatsAnEsimModal, setShowWhatsAnEsimModal] = useState(false)
  const [showCanIkeepMyNumberModal, setShowCanIkeepMyNumberModal] =
    useState(false)

  return (
    <Container background={theme.colors.wildSand}>
      <EsimCompatibilityModal
        display={showEsimCompatibleModal}
        setDisplay={setShowEsimCompatibleModal}
      />
      <CanIkeepMyNumberModal
        display={showCanIkeepMyNumberModal}
        setDisplay={setShowCanIkeepMyNumberModal}
      />
      <WhatsAnEsimModal
        display={showWhatsAnEsimModal}
        setDisplay={setShowWhatsAnEsimModal}
      />
      <Box
        maxWidth={theme.layout.readableWidth}
        pb={theme.spacing.small}
        pt={theme.spacing.small}
      >
        <Typography
          as="h1"
          pb={theme.spacing.small}
          pt={theme.spacing.small}
          variant="megaphone"
        >
          {content.chooseSim.title}
        </Typography>

        <Typography
          as="p"
          pb={theme.spacing.small}
          pt={theme.spacing.small}
          variant="speak"
        >
          {content.chooseSim.subTitleV2}
        </Typography>

        <RadioGroup
          opaque
          ariaLabel="Choose your SIM type, eSIM or physical SIM card"
          flexDirection="row"
          name="radioGroupWithTopRightRadio"
          radioPosition="topLeft"
        >
          <OptionRadio
            fullWidth
            active={chosenSim === 'eSIM'}
            badge={{ text: content.chooseSim.recommendedBadgeText }}
            label={content.chooseSim.eSimRadioLabel}
            typography={'speak-up'}
            uniqueId="eSIM"
            onClick={() => {
              if (chosenSim === 'eSIM') return
              setChosenSim('eSIM')
              customEvent('sim_onboarding_select_sim_type', {
                simType: 'esim',
              })
            }}
          />
          <OptionRadio
            fullWidth
            active={chosenSim === 'pSIM'}
            label="pSIM"
            typography={'speak-up'}
            uniqueId="pSIM"
            onClick={() => {
              if (chosenSim === 'pSIM') return
              setChosenSim('pSIM')
              customEvent('sim_onboarding_select_sim_type', {
                simType: 'psim',
              })
            }}
          >
            {content.chooseSim.plasticSimRadioLabel}
          </OptionRadio>
        </RadioGroup>

        {chosenSim === 'pSIM' && (
          <>
            <Typography
              as="p"
              pb={theme.spacing.small}
              pt={theme.spacing.medium}
              variant="speakUp"
            >
              {content.chooseSim.PlasticSimTitle}
            </Typography>
            <Typography as="p" variant="speak">
              {content.chooseSim.PlasticSimText1}
            </Typography>
            <Typography
              as="p"
              pb={theme.spacing.small}
              pt={theme.spacing.small}
              variant="speak"
            >
              {content.chooseSim.downloadAppForPlasticSim}
            </Typography>
          </>
        )}
        {chosenSim === 'eSIM' && (
          <>
            <Typography
              as="p"
              pb={theme.spacing.small}
              pt={theme.spacing.medium}
              variant="speakUp"
            >
              {content.chooseSim.EsimTitle}
            </Typography>
            <UnorderedList iconKey="Flash" iconSize="medium">
              <Typography as={'p'} variant="speak">
                {content.chooseSim.EsimText1}
              </Typography>
            </UnorderedList>
            <UnorderedList iconKey="Environment" iconSize="medium">
              <Typography as={'p'} variant="speak">
                {content.chooseSim.EsimText2}
              </Typography>
            </UnorderedList>
            <UnorderedList iconKey="Sun" iconSize="medium">
              <Typography as={'p'} variant="speak">
                {content.chooseSim.EsimText3}
              </Typography>
            </UnorderedList>
            <Typography
              as={'p'}
              pb={theme.spacing.small}
              pt={theme.spacing.small}
            >
              {content.chooseSim.downloadAppForEsim}
            </Typography>
            <Alert
              isMinor
              ariaLabel="eSIM compatibility disclaimer"
              borderRadius="small"
              mb={theme.spacing.small}
              mt={theme.spacing.small}
              noIcon={false}
              variant="info"
            >
              <Typography as={'h2'} variant="speakUp">
                {content.chooseSim.eSimCompatibilityAlertTitle}
              </Typography>
              <StyledTypography as={'p'}>
                {content.chooseSim.eSimCompatibilityAlertDescription}
              </StyledTypography>
              <Box
                pt={theme.spacing.tiny}
                sx={{ position: 'relative', left: '-2px' }}
                width={'fit-content'}
              >
                <Link
                  as={'button'}
                  onClick={() => {
                    setShowEsimCompatibleModal(true)
                    customEvent('sim_onboarding_view_esim_compatibility', {})
                  }}
                >
                  {content.chooseSim.eSimCompatibilityAlertModalLink}
                </Link>
              </Box>
            </Alert>
          </>
        )}

        <Flex pb={theme.spacing.small} pt={theme.spacing.small}>
          <Button
            isFullWidth={{ down: 'medium' }}
            textAlign="center"
            variant="primary"
            onClick={() => {
              customEvent('sim_onboarding_submit_sim_type', {
                simType: chosenSim === 'eSIM' ? 'esim' : 'psim',
              })
              if (enableSnowplow) {
                snowplowEvents.ctaInteract(
                  {
                    type: 'button',
                    interaction: 'click',
                    name: 'Choose Sim Submit Button',
                    id: 'sim_onboarding_submit_sim_type',
                    text: content.chooseSim.CTAbutton,
                  },
                  [
                    {
                      schema: 'iglu:com.giffgaff/product/jsonschema/1-0-0',
                      data: {
                        sku:
                          chosenSim === 'eSIM'
                            ? 'Single_ESIM_Pack_unprovisioned'
                            : 'Single_SIM_Pack_unprovisioned',
                        product_type: 'sim',
                        category: chosenSim === 'eSIM' ? 'esim' : 'psim',
                      },
                    },
                  ],
                )
              }
              mfeRedirect(buildNextPageUrl(sku, chosenSim, isLoggedIn))
            }}
          >
            {content.chooseSim.CTAbutton}
          </Button>
        </Flex>

        <Typography as="p" py={theme.spacing.small} variant="speakUp">
          {content.faq.title}
        </Typography>

        <Flex flexDirection="column" pb={theme.spacing.small}>
          <LinkListCard
            ariaLabel={content.faq.ariaLabel}
            items={[
              {
                key: 'FAQ1',
                text: content.chooseSim.FAQText1,
                onClick: () => {
                  setShowWhatsAnEsimModal(true)
                  customEvent('sim_onboarding_view_faq', {
                    question: content.chooseSim.FAQText1,
                  })
                },
              },
              {
                key: 'FAQ2',
                text: content.chooseSim.FAQText2,
                onClick: () => {
                  setShowCanIkeepMyNumberModal(true)
                  customEvent('sim_onboarding_view_faq', {
                    question: content.chooseSim.FAQText2,
                  })
                },
              },
            ]}
          />
        </Flex>
      </Box>
    </Container>
  )
}

export default ChooseSim
